import React, { useState, useEffect, useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, AreaChart, Area, LabelList,
    PieChart, Pie, Cell
} from 'recharts';
import {
    getFirestore, collection, query, where, getDocs, orderBy, Timestamp
} from 'firebase/firestore';
import { Form, Row, Col, Spinner, Button, Offcanvas } from 'react-bootstrap';
import { FaClock, FaCalendarAlt, FaShieldAlt, FaBan, FaUsers, FaDoorOpen } from 'react-icons/fa';

const AccessReports = (props) => {
    const { name, filex, apc } = props;
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    const todayStr = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(todayStr);
    const [endDate, setEndDate] = useState(todayStr);

    const db = getFirestore();
    
    // Consistent APC Color Palette
    const COLORS = ['#409da0cb', '#d1a054c9', '#0dcaf0cb', '#a81d32cb'];

    const handleClose = () => { if (props.onToggle) props.onToggle(); };
    const handleShow = () => { if (props.onToggle) props.onToggle(); };

    const fetchReportData = async () => {
        setLoading(true);
        try {
            const selectedYear = new Date(startDate).getFullYear();
            const logsRef = collection(db, `access_logs_${selectedYear}`);
            const start = new Date(startDate + "T00:00:00");
            const end = new Date(endDate + "T23:59:59");

            const q = query(
                logsRef,
                where("scannedAt", ">=", Timestamp.fromDate(start)),
                where("scannedAt", "<=", Timestamp.fromDate(end)),
                orderBy("scannedAt", "desc")
            );

            const querySnapshot = await getDocs(q);
            const fetchedLogs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLogs(fetchedLogs);
        } catch (err) {
            console.error("Report Fetch Error:", err);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (props.isOpen) fetchReportData();
    }, [props.isOpen, startDate, endDate]);

    const reportData = useMemo(() => {
        const monthlyMap = {};
        const hourlyMap = Array.from({ length: 24 }, (_, i) => ({
            hour: i,
            label: i >= 12 ? `${i === 12 ? 12 : i - 12} PM` : `${i === 0 ? 12 : i} AM`,
            Members: 0,
            Guests: 0,
            Bypassed: 0
        }));
        
        const stats = { 
            total: 0, members: 0, guests: 0, denied: 0, bypassed: 0, 
            bypassMembers: 0, bypassGuests: 0,
            deniedExpired: 0, deniedFlagged: 0, deniedIncident: 0, deniedBanned: 0,
            estimatedInClub: 0, totalCheckedOut: 0
        };

        // 🟢 UNIQUE HEADCOUNT ENGINE: Tracks the absolute latest chronological real-time status of each unique identifier
        const uniquePatronPresenceMap = new Map();

        logs.forEach(log => {
            if (log.status === 'cancelled') return;
            
            const date = log.scannedAt?.toDate ? log.scannedAt.toDate() : new Date();
            const monthYear = date.toLocaleString('default', { month: 'short', year: '2-digit' });
            const hour = date.getHours();
            
            const isBypass = log.status === 'bypassed';
            const isDenied = log.isExpired || log.isFlagged || log.hasIncident || log.isBanned;

            stats.total++;

            if (isBypass) {
                stats.bypassed++;
                if (log.entryType === 'Guest') stats.bypassGuests++;
                else stats.bypassMembers++;
            } else if (isDenied) {
                stats.denied++;
                if (log.isExpired) stats.deniedExpired++;
                if (log.isFlagged) stats.deniedFlagged++;
                if (log.hasIncident) stats.deniedIncident++;
                if (log.isBanned) stats.deniedBanned++;
            } else if (log.entryType === 'Guest') {
                stats.guests++;
            } else {
                stats.members++;
            }

            // 🟢 DEDUPLICATION LOGIC FOR CAPACITY: Group by rawPayload/identifier.
            // Since firestore query is ordered by 'scannedAt' desc, the FIRST log we hit for a user is their LATEST status.
            if (!isDenied || isBypass) {
                const patronId = (log.rawPayload || log.id).toLowerCase();
                if (!uniquePatronPresenceMap.has(patronId)) {
                    uniquePatronPresenceMap.set(patronId, {
                        hasCheckedOut: !!log.checkedOutAt
                    });
                }
            }

            if (!monthlyMap[monthYear]) {
                monthlyMap[monthYear] = { name: monthYear, Members: 0, Guests: 0, Denied: 0, Bypass: 0, total: 0 };
            }

            if (isBypass) monthlyMap[monthYear].Bypass++;
            else if (isDenied) monthlyMap[monthYear].Denied++;
            else if (log.entryType === 'Guest') monthlyMap[monthYear].Guests++;
            else monthlyMap[monthYear].Members++;

            monthlyMap[monthYear].total = (monthlyMap[monthYear].Members || 0) + (monthlyMap[monthYear].Guests || 0) + (monthlyMap[monthYear].Denied || 0) + (monthlyMap[monthYear].Bypass || 0);

            // 🟢 PEAK TIMES REMAIN UNTOUCHED: Retains historical data density integrity across distribution slopes
            if (!isDenied || isBypass) {
                if (isBypass) hourlyMap[hour].Bypassed++;
                else if (log.entryType === 'Guest') hourlyMap[hour].Guests++;
                else hourlyMap[hour].Members++;
            }
        });

        // 🟢 COMPILE DEDUPLICATED CAPACITY SCORES
        uniquePatronPresenceMap.forEach((status) => {
            if (status.hasCheckedOut) {
                stats.totalCheckedOut++;
            } else {
                stats.estimatedInClub++;
            }
        });

        const bypassPieData = [
            { name: 'Member Bypass', value: stats.bypassMembers },
            { name: 'Guest Bypass', value: stats.bypassGuests }
        ].filter(d => d.value > 0);

        const denialPieData = [
            { name: 'Expired', value: stats.deniedExpired },
            { name: 'Flagged', value: stats.deniedFlagged },
            { name: 'Incident', value: stats.deniedIncident },
            { name: 'Banned', value: stats.deniedBanned }
        ].filter(d => d.value > 0);

        return { monthly: Object.values(monthlyMap).reverse(), hourly: hourlyMap, stats, bypassPieData, denialPieData };
    }, [logs]);

    const customTooltipStyle = {
        contentStyle: { backgroundColor: '#111', border: '1px solid #444', borderRadius: '8px', color: '#fff', fontSize: '10px' },
        itemStyle: { color: '#fff' },
        cursor: { fill: 'rgba(255,255,255,0.05)' }
    };

    return (
        <>
            <Button onClick={handleShow} className="hoverX shadow-sm">
                <span className="circle-btn centerCircle">📊</span>
                Access
            </Button>

            <Offcanvas show={props.isOpen} onHide={handleClose} placement="end" className="bg-dark text-light" style={{ width: '100%', maxWidth: '550px', borderLeft: '1px solid #333' }}>
                <Offcanvas.Header closeButton closeVariant="white" className="border-bottom border-secondary">
                    <Offcanvas.Title>
                        <h3 className="modified xTitle animated rollIn">Access Reportz</h3>
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-3">
                    <style>{`
                        .report-input { background: rgba(255,255,255,0.05) !important; border: 1px solid #444 !important; color: white !important; font-size: 0.75rem; padding: 5px; }
                        .pulse-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 8px 4px; text-align: center; height: 100%; min-height: 60px; display: flex; flex-direction: column; justify-content: center; }
                        .capacity-card { background: linear-gradient(135deg, rgba(64, 157, 160, 0.1), rgba(13, 202, 240, 0.05)); border: 1px solid #409da0aa; border-radius: 12px; padding: 10px; margin-bottom: 15px; }
                        .chart-container { background: rgba(255,255,255,0.02); border: 1px solid #333; border-radius: 12px; padding: 12px; margin-bottom: 15px; }
                        .stat-label { font-size: 0.5rem; color: #888; text-transform: uppercase; letter-spacing: 0.5px; display: block; line-height: 1.2; }
                        .stat-val { font-size: 1rem; font-weight: bold; display: block; margin-top: 2px; }
                        .today-btn { font-size: 0.6rem; padding: 4px 10px; background: rgba(64, 157, 160, 0.2); border: 1px solid #409da0cb; color: #409da0cb; border-radius: 4px; }
                        @media (max-width: 400px) {
                            .stat-val { font-size: 0.9rem; }
                            .stat-label { font-size: 0.45rem; }
                        }
                    `}</style>

                    <div className="chart-container">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="stat-label m-0"><FaCalendarAlt className="me-1" /> Range</label>
                            <button className="today-btn" onClick={() => { setStartDate(todayStr); setEndDate(todayStr); }}>TODAY</button>
                        </div>
                        <Row className="g-2">
                            <Col xs={6}><Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="report-input" /></Col>
                            <Col xs={6}><Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="report-input" /></Col>
                        </Row>
                    </div>

                    {/* LIVE OCCUPANCY & CAPACITY REFERENCE SECTION */}
                    <div className="capacity-card">
                        <Row className="align-items-center text-center g-2">
                            <Col xs={6} className="border-end border-secondary border-opacity-25">
                                <span className="stat-label text-info"><FaUsers className="me-1" /> Estimated Inside</span>
                                <span className="stat-val text-info" style={{ fontSize: '1.6rem', lineHeight: '1.2' }}>
                                    {reportData.stats.estimatedInClub}
                                </span>
                                <small className="text-muted" style={{ fontSize: '0.5rem', display: 'block', marginTop: '2px' }}>Unique Headcount Metric</small>
                            </Col>
                            <Col xs={6}>
                                <span className="stat-label text-muted"><FaDoorOpen className="me-1" /> Checked-Out</span>
                                <span className="stat-val text-muted" style={{ fontSize: '1.3rem', lineHeight: '1.2' }}>
                                    {reportData.stats.totalCheckedOut}
                                </span>
                                <small className="text-muted" style={{ fontSize: '0.5rem', display: 'block', marginTop: '2px' }}>Left Geofence Range</small>
                            </Col>
                        </Row>
                    </div>

                    {/* STATS GRID */}
                    <div className="row g-2 mb-3">
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-info"><span className="stat-label">Members</span><span className="stat-val text-info">{reportData.stats.members}</span></div>
                        </div>
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-warning"><span className="stat-label">Guests</span><span className="stat-val text-warning">{reportData.stats.guests}</span></div>
                        </div>
                        <div className="col-4 col-md-2">
                            <div className="pulse-card border-primary"><span className="stat-label">Bypass</span><span className="stat-val text-primary">{reportData.stats.bypassed}</span></div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="pulse-card border-danger"><span className="stat-label">Denied</span><span className="stat-val text-danger">{reportData.stats.denied}</span></div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="pulse-card border-secondary"><span className="stat-label">Total</span><span className="stat-val text-white">{reportData.stats.total}</span></div>
                        </div>
                    </div>

                    <Row className="g-2 mb-3">
                        <Col xs={12} sm={6}>
                            <div className="chart-container h-100">
                                <h6 className="stat-label mb-2 text-center"><FaShieldAlt className="me-1" /> Bypass Type</h6>
                                <ResponsiveContainer width="100%" height={120}>
                                    <PieChart>
                                        <Pie data={reportData.bypassPieData} cx="50%" cy="50%" innerRadius={25} outerRadius={40} paddingAngle={5} dataKey="value">
                                            {reportData.bypassPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...customTooltipStyle} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="text-center" style={{ fontSize: '0.55rem' }}>
                                    <span style={{ color: COLORS[0] }}>MEM: {reportData.stats.bypassMembers}</span> | <span style={{ color: COLORS[1] }}>GUEST: {reportData.stats.bypassGuests}</span>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="chart-container h-100">
                                <h6 className="stat-label mb-2 text-center"><FaBan className="me-1" /> Denial Reasons</h6>
                                <ResponsiveContainer width="100%" height={120}>
                                    <PieChart>
                                        <Pie data={reportData.denialPieData} cx="50%" cy="50%" innerRadius={25} outerRadius={40} paddingAngle={5} dataKey="value">
                                            {reportData.denialPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[(index + 1) % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...customTooltipStyle} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="text-center" style={{ fontSize: '0.55rem', display: 'flex', justifyContent: 'space-between', padding: '0 5px' }}>
                                    <span style={{ color: COLORS[1] }}>EXP</span><span style={{ color: COLORS[2] }}>FLG</span><span style={{ color: COLORS[3] }}>INC</span><span style={{ color: COLORS[0] }}>BAN</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <div className="chart-container">
                        <h6 className="stat-label mb-3 text-center">Monthly Distribution</h6>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={reportData.monthly} margin={{ top: 10, right: 5, left: -35, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                                <XAxis dataKey="name" stroke="#555" fontSize={8} />
                                <YAxis stroke="#555" fontSize={8} />
                                <Tooltip {...customTooltipStyle} />
                                <Bar dataKey="Members" stackId="a" fill={COLORS[0]} />
                                <Bar dataKey="Guests" stackId="a" fill={COLORS[1]} />
                                <Bar dataKey="Bypass" stackId="a" fill={COLORS[2]} />
                                <Bar dataKey="Denied" stackId="a" fill={COLORS[3]} radius={[4, 4, 0, 0]}>
                                    <LabelList dataKey="total" position="top" style={{ fill: '#888', fontSize: '9px' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="chart-container">
                        <h6 className="stat-label mb-3 text-center"><FaClock className="me-1" /> Peak Times</h6>
                        <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={reportData.hourly} margin={{ top: 10, right: 5, left: -35, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                                <XAxis dataKey="label" stroke="#555" fontSize={7} interval={4} />
                                <YAxis stroke="#555" fontSize={7} />
                                <Tooltip {...customTooltipStyle} />
                                <Area type="monotone" dataKey="Members" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.2} strokeWidth={2} />
                                <Area type="monotone" dataKey="Guests" stroke={COLORS[1]} fill={COLORS[1]} fillOpacity={0.1} strokeWidth={2} />
                                <Area type="monotone" dataKey="Bypassed" stroke={COLORS[2]} fill={COLORS[2]} fillOpacity={0.1} strokeWidth={2} strokeDasharray="4 4" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default AccessReports;